import React, { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, A11y } from "swiper/modules";

import "swiper/css";

function YearHistory() {
  const { t } = useTranslation();

  const slides = useMemo(
    () => [
      { year: 2015, textKey: "about_us.28" },
      { year: 2016, textKey: "about_us.29" },
      { year: 2017, textKey: "about_us.30" },
      { year: 2018, textKey: "about_us.31" },
      { year: 2019, textKey: "about_us.32" },
      { year: 2020, textKey: "about_us.33" },
      { year: 2021, textKey: "about_us.34" },
      { year: 2022, textKey: "about_us.35" },
      { year: 2023, textKey: "about_us.36" },
      { year: 2024, textKey: "about_us.37" },
    ],
    []
  );

  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1280px)");
    const handler = (e) => setIsDesktop(e.matches);

    handler(mq);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const getVisibleSlides = (allSlides, currentIndex, desktop) => {
    if (desktop) return allSlides;

    const from = Math.max(currentIndex - 1, 0);
    const to = Math.min(currentIndex + 1, allSlides.length - 1);

    return allSlides.slice(from, to + 1);
  };

  const visibleSlides = useMemo(
    () => getVisibleSlides(slides, activeIndex, isDesktop),
    [slides, activeIndex, isDesktop]
  );

  const goTo = (idx) => {
    const swiper = swiperRef.current;
    if (!swiper) return;
    swiper.slideToLoop(idx);
  };

  return (
    <div className="relative overflow-hidden mt-[80px] xl:mt-[140px]">
      <div className="max-w-[1220px] mx-[20px] xl:mx-auto">
        <div className="mb-8">
          <div className="text-[#061A77] text-[20px] leading-[1.2] xl:text-[32px] font-[500]">
            {t("about_us.26")}
          </div>
          <h2 className="text-[32px] xl:text-[48px] leading-[1.2] font-[500] text-[#061A77]">
            {t("about_us.27")}
          </h2>
        </div>

        <div className="relative">
          <Swiper
            modules={[Autoplay, Navigation, A11y]}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
              setActiveIndex(swiper.realIndex ?? 0);
            }}
            onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
            slidesPerView={1}
            spaceBetween={18}
            loop
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            navigation={{
              prevEl: ".yearhist-prev",
              nextEl: ".yearhist-next",
            }}
            breakpoints={{
              1024: { slidesPerView: 3, spaceBetween: 22 },
            }}
            className="pb-[100px]" // место под нижнюю пагинацию
          >
            {slides.map((s, index) => (
              <SwiperSlide key={s.year}>
                <div className="h-[289px] bg-[#F1F7FE] rounded-[4px] p-7 xl:p-9 flex flex-col items-start">
                  <div className={`transition-all rounded-[4px] border border-[#061A77] ${activeIndex === index ? "bg-[#061A77]" : "bg-transparent"} px-[16px]`}>
                    <p className={`transition-all font-[500] poppins text-[16px] xl:text-[20px] ${activeIndex === index ? "text-[#FFFFFF]" : "text-[#061A77]"}`}>{s.year}</p>
                  </div>

                  <p className="mt-6 text-[#000000B2] text-[14px] inter">
                    {t(s.textKey)}
                  </p>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <div className="xl:mt-[20px]">
            <div className="mx-auto">
              <div
                className="
                    flex items-center justify-between gap-3 py-2
                    overflow-hidden xl:overflow-visible
                "
              >
                <button
                  className="yearhist-prev w-[46px] h-[22px] xl:w-[56px] xl:h-[22px]
                   rounded-[2px] border border-[#061A77] text-[#061A77]
                   flex items-center justify-center
                   hover:bg-[#061A77] hover:text-white transition"
                  type="button"
                >
                  ←
                </button>
                <div className="flex items-center gap-4 xl:gap-6">
                  {visibleSlides.map((s) => {
                    const idx = slides.findIndex((x) => x.year === s.year);
                    const isActive = idx === activeIndex;

                    return (
                      <button
                        key={s.year}
                        type="button"
                        onClick={() => goTo(idx)}
                        className={[
                          "text-[12px] xl:text-[14px] inter transition",
                          isActive
                            ? "text-[#061A77]"
                            : "text-[#061A774D] hover:text-[#061A77]",
                        ].join(" ")}
                      >
                        {s.year}
                      </button>
                    );
                  })}
                </div>
                <button
                  className="yearhist-next w-[46px] h-[22px] xl:w-[56px] xl:h-[22px]
                   rounded-[2px] bg-[#061A77] text-white
                   flex items-center justify-center
                   hover:opacity-90 transition"
                  type="button"
                >
                  →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default YearHistory;
